import React, { useState } from 'react';
import { Plus, Trash2, Calendar, FileText, Loader, User, Pencil, Check, X } from 'lucide-react';
import { customerService } from '../api/customerService';
import { useNotification } from '../contexts/NotificationContext';
import { HasPermission } from '../hooks/usePermission';
import DeleteConfirmModal from './DeleteConfirmModal';

export default function CustomerNotes({ customerId, notes, onNoteAdded, onNoteDeleted }) {
  const [newNote, setNewNote] = useState('');
  const [loading, setLoading] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [noteToDelete, setNoteToDelete] = useState(null);
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [editingText, setEditingText] = useState('');
  const { showNotification } = useNotification();

  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    
    try {
      setLoading(true);
      await customerService.createNote(customerId, newNote.trim());
      showNotification('success', 'Catatan berhasil ditambahkan');
      setNewNote('');
      if (onNoteAdded) onNoteAdded();
    } catch (err) {
      console.error('Error adding note:', err);
      showNotification('error', err.response?.data?.message || 'Gagal menambahkan catatan');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!noteToDelete) return;
    try {
      setLoading(true);
      await customerService.deleteNote(customerId, noteToDelete.id);
      showNotification('success', 'Catatan berhasil dihapus');
      setIsDeleteModalOpen(false);
      setNoteToDelete(null);
      if (onNoteDeleted) onNoteDeleted();
    } catch (err) {
      console.error('Error deleting note:', err);
      showNotification('error', err.response?.data?.message || 'Gagal menghapus catatan');
    } finally {
      setLoading(false);
    }
  };

  const handleEditNote = (note) => {
    setEditingNoteId(note.id);
    setEditingText(note.note || '');
  };

  const handleUpdateNote = async () => {
    const text = editingText.trim();
    if (!editingNoteId || !text) return;

    try {
      setLoading(true);
      await customerService.updateNote(customerId, editingNoteId, text);
      showNotification('success', 'Catatan berhasil diubah');
      setEditingNoteId(null);
      setEditingText('');
      if (onNoteAdded) onNoteAdded();
    } catch (err) {
      console.error('Error updating note:', err);
      showNotification('error', err.response?.data?.message || 'Gagal mengubah catatan');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Intl.DateTimeFormat('id-ID', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(new Date(dateString));
  };

  return (
    <>
      <div className="bg-white rounded-2xl ring-1 ring-slate-200/70 shadow-sm mb-6 overflow-hidden flex flex-col">
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
          <h3 className="text-base font-semibold text-slate-900 tracking-tight flex items-center gap-2">
            <FileText className="w-5 h-5 text-teal-600" />
            Catatan Pelanggan
          </h3>
        </div>
        
        <div className="p-6 flex-1 flex flex-col gap-6">
          <HasPermission permission="customer.update">
            <div className="flex gap-3">
              <input
                type="text"
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
                placeholder="Tambahkan catatan baru..."
                className="flex-1 px-4 py-2 bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-500 transition-colors"
                onKeyPress={(e) => e.key === 'Enter' && handleAddNote()}
                disabled={loading}
              />
              <button
                onClick={handleAddNote}
                disabled={!newNote.trim() || loading}
                className="px-4 py-2 bg-teal-600 text-white rounded-lg flex items-center gap-2 hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {loading ? <Loader className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                <span>Tambah</span>
              </button>
            </div>
          </HasPermission>

          <div className="flex flex-col gap-3">
            {(!notes || notes.length === 0) ? (
              <div className="text-center py-8 text-slate-400 text-sm border-2 border-dashed border-slate-100 rounded-xl">
                Belum ada catatan untuk pelanggan ini
              </div>
            ) : (
              notes.map((note) => (
                <div key={note.id} className="flex items-start justify-between p-4 bg-slate-50/50 rounded-xl ring-1 ring-slate-200/60 hover:ring-slate-300 transition-all group">
                   <div className="flex flex-col gap-1 min-w-0 flex-1 mr-4">
                     {editingNoteId === note.id ? (
                       <div className="flex flex-col gap-2">
                         <textarea
                           value={editingText}
                           onChange={(e) => setEditingText(e.target.value)}
                           className="w-full min-h-20 px-3 py-2 text-sm text-slate-700 bg-white border border-teal-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500/30"
                           autoFocus
                           disabled={loading}
                         />
                         <div className="flex items-center gap-2">
                           <button
                             onClick={handleUpdateNote}
                             disabled={!editingText.trim() || loading}
                             className="px-3 py-1.5 bg-teal-600 text-white rounded-lg text-xs font-medium flex items-center gap-1.5 hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed"
                           >
                             {loading ? <Loader className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                             Simpan
                           </button>
                           <button
                             onClick={() => { setEditingNoteId(null); setEditingText(''); }}
                             disabled={loading}
                             className="px-3 py-1.5 bg-slate-100 text-slate-600 rounded-lg text-xs font-medium flex items-center gap-1.5 hover:bg-slate-200 disabled:opacity-50"
                           >
                             <X className="w-3.5 h-3.5" />
                             Batal
                           </button>
                         </div>
                       </div>
                     ) : (
                       <p className="text-sm text-slate-700 whitespace-pre-wrap">{note.note}</p>
                     )}
                    <div className="flex items-center gap-4 mt-2">
                      <span className="flex items-center gap-1.5 text-xs text-slate-400 font-medium">
                        <Calendar className="w-3.5 h-3.5" />
                        {formatDate(note.created_at)}
                      </span>
                      <span className="flex items-center gap-1.5 text-xs text-slate-500 font-medium bg-white px-2.5 py-1 rounded-full ring-1 ring-slate-200/80 shadow-sm">
                        <User className="w-3.5 h-3.5 text-slate-400" />
                        {note.created_by}
                      </span>
                    </div>
                  </div>
                  
                   <HasPermission permission="customer.update">
                     <div className="flex items-center gap-2 shrink-0">
                       {editingNoteId !== note.id && (
                         <button
                           onClick={() => handleEditNote(note)}
                           disabled={loading}
                           className="p-2 text-teal-600 bg-teal-50 hover:bg-teal-100 rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center"
                           title="Edit Catatan"
                         >
                           <Pencil className="w-4 h-4" />
                         </button>
                       )}
                       <button
                         onClick={() => {
                           setNoteToDelete(note);
                           setIsDeleteModalOpen(true);
                         }}
                         disabled={loading}
                         className="p-2 text-rose-600 bg-rose-50 hover:bg-rose-100 rounded-lg transition-colors opacity-100 disabled:opacity-50 flex items-center justify-center"
                         title="Hapus Catatan"
                       >
                         <Trash2 className="w-4 h-4" />
                       </button>
                     </div>
                   </HasPermission>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      <DeleteConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setNoteToDelete(null);
        }}
        onConfirm={handleDeleteConfirm}
        data={noteToDelete ? { id: noteToDelete.id } : null}
      />
    </>
  );
}
